import type { StudioId } from '@/domain/types';

// The studio switcher's selection: a specific studio, or the network-wide "all".
// Kept dependency-free (no store import) so sessionStore can reuse the type
// without a circular import — the React hook lives in state/useActiveStudio.ts.
export const ALL_STUDIOS_SCOPE = 'all' as const;

export type ActiveStudio = StudioId | typeof ALL_STUDIOS_SCOPE;

// An unset selection is treated the same as "all" so screens never blank out
// before a persona has picked a studio.
export function isAllStudios(active: ActiveStudio | null | undefined): boolean {
  return active == null || active === ALL_STUDIOS_SCOPE;
}

// The set of studio ids a screen should read for the current scope: the single
// picked studio, or — for the network-wide "all"/unset scope — the persona's full
// assigned set. Screens pass this as `studioIds` so the top-bar switcher, not the
// static assignedStudios permission list, drives what's shown.
//
// branding-3: pass the studio map and "all" leaves archived studios out.
export function studioScopeIds(
  active: ActiveStudio | null | undefined,
  assignedStudios: StudioId[],
  studios?: Record<string, { id: string; archived?: boolean } | undefined>,
): StudioId[] {
  if (!isAllStudios(active)) return [active as StudioId];
  return studios ? assignedStudios.filter((id) => !studios[id]?.archived) : assignedStudios;
}

// Filter any studio-tagged collection down to the active studio. The network-wide
// "all" (and an unset scope) pass everything through unchanged.
export function scopeByStudio<T extends { studioId: StudioId }>(
  items: T[],
  active: ActiveStudio | null | undefined,
): T[] {
  if (isAllStudios(active)) return items;
  return items.filter((item) => item.studioId === active);
}

/**
 * The scope after a single-studio page (`/calendar/:id`, `/studio/:id`) opens
 * (B9 / 14ym9bv75n8). "All studios" is a choice the user made in the switcher, so
 * a route never replaces it — the page shows its own studio from the URL and the
 * rest of the app stays network-wide. A different concrete pick follows the page
 * (a deep link or a rail hop keeps the switcher honest). The network calendar
 * (`all`) and a missing param change nothing.
 */
export function scopeOnRoute(
  active: ActiveStudio | null,
  routeStudio: string | undefined,
): ActiveStudio | null {
  if (!routeStudio || routeStudio === ALL_STUDIOS_SCOPE) return active;
  if (isAllStudios(active)) return active;
  return routeStudio as StudioId;
}

/** The bits of a studio record the active-studio helpers read. */
interface StudioLike {
  id: StudioId;
  archived?: boolean;
}

/**
 * branding-3: the ONE "which studios are open for business" rule. An archived
 * studio (soft-deleted in Settings) drops out of every switcher, select and
 * network-wide list. An id the map doesn't hold yet (studios not loaded) is kept,
 * so nothing blanks out before hydration. Keeps `among`'s order when given (a
 * persona's assigned studios), otherwise the studio map's order.
 */
export function activeStudioIds(
  studios: Record<string, StudioLike | undefined>,
  among?: readonly StudioId[],
): StudioId[] {
  const ids = among ?? (Object.keys(studios) as StudioId[]);
  return ids.filter((id) => !studios[id]?.archived);
}

/**
 * Studio records for a select's options: the active studios (optionally within
 * `among`), plus the select's CURRENT value even when that studio is archived,
 * so an existing record never silently shows (or saves) a different studio.
 */
export function studioOptions<T extends StudioLike>(
  studios: Record<string, T | undefined>,
  among?: readonly StudioId[],
  currentValue?: string | null,
): T[] {
  const ids = activeStudioIds(studios, among);
  if (currentValue && !ids.includes(currentValue as StudioId) && studios[currentValue]) {
    ids.push(currentValue as StudioId);
  }
  return ids.flatMap((id) => (studios[id] ? [studios[id]!] : []));
}
